import React, { useEffect, useState } from "react";
import { Card, CardContent } from "@shared/ui/card";
import { Input } from "@shared/ui/input";
import { Button } from "@shared/ui/button";
import { Badge } from "@shared/ui/badge";
import { Separator } from "@shared/ui/separator";
import { Search } from "lucide-react";
import { ValueLabel } from "@features/ax/shared/ValueLabel";
import { useBillChangesDetails, useBillSearch } from "../hook";
import { useCurrency, useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { useDebounce } from "@shared/hooks/useDebounce";
import { getStatusBadgeClass } from "@shared/utils/statusUtils";
import { useSearchParams } from "react-router-dom";
import { normalizeDate } from "@shared/utils/dateUtils";
import { toCamelCase } from "@shared/utils/caseConverter";

interface StepHelpers {
  getStepData: (index: number) => any;
  setStepData: (index: number, data: any) => void;
}

interface BillSelectionStepProps {
  stepHelpers?: StepHelpers;
  currentStepIndex?: number;
  onNext?: () => void;
  setValidationError?: (error: string) => void;
  clearValidationError?: () => void;
}

const getBillDetailsConfig = (bill: any) => [
  { label: "Consumer name", value: `${bill.firstName} ${bill.lastName}` },
  { label: "Account no", value: bill.remoteConsumerNumber },
  { label: "Email", value: bill.consumerEmail },
  { label: "Phone", value: bill.consumerContactNo },
  { label: "Bill date", value: bill.billMonth },
];

const BillSelectionStep: React.FC<BillSelectionStepProps> = ({
  stepHelpers,
  currentStepIndex = 0,
  onNext,
  setValidationError,
  clearValidationError,
}) => {
  const currency = useCurrency();
  const remoteUtilityId = useRemoteUtilityId();
  const billSelected = stepHelpers?.getStepData(currentStepIndex);
  const [searchQuery, setSearchQuery] = useState("");
  const searchData = useDebounce(searchQuery, 300)
  const [selectedBill, setSelectedBill] = useState<any | null>(billSelected?.selectedBill);
  const [isDataInitialized, setIsDataInitialized] = useState(false);
  const {data: billsList} = useBillSearch({
    page: 1,
    searchData,
    remoteUtilityId
  })

  const [searchParams] = useSearchParams();

const action = searchParams.get("action");
const correctionId = searchParams.get("correction_id"); 

const { data: previewBill } = useBillChangesDetails(correctionId);

 useEffect(() => {
  if (action !== "draft" || !previewBill) return;

  const billNumber = previewBill?.originalBill?.billNumber;
  if (billNumber) {
    setSearchQuery(billNumber);
  }
}, [action, previewBill]);

  // Once bill is found in search results, set all step data
  useEffect(() => {
    if (action === "draft" && correctionId && previewBill && billsList?.result) {
      const billNumber = previewBill?.originalBill?.billNumber;
      const foundBill = billsList?.result?.find(bill => bill.billNumber === billNumber);
      const typeMap = { consumption: 0, lateFees: 2, consumerData: 3, serviceCharges: 1, template: 4 };
      if (foundBill) {
        // Set Step 0 data (current step)
        setSelectedBill(foundBill);
        stepHelpers?.setStepData(0, {
          selectedBill: foundBill,
          correctionId: correctionId
        });
        
        // Set Step 1 data - derive correction types from applied_corrections
        const appliedCorrections = previewBill?.appliedCorrections;
        // const selectedTypes = Object.keys(appliedCorrections ?? {})
        //   .map((key) => typeMap[key as keyof typeof typeMap])
        //   .filter((v) => v !== undefined);
        const selectedTypes = previewBill?.correctionTypes;
        stepHelpers?.setStepData(1, {
          selectedCorrectionTypes: selectedTypes
        });
        
        // Set Step 2 data - transform each correction type
        const step2Data: any = {};
        
        // Consumption data
        if (appliedCorrections.consumption) {
          step2Data.consumption = {
            meters: appliedCorrections?.consumption?.meters?.map(meter => ({
              meter_id: meter?.meterId,
              corrected_reading: meter.currentReading.corrected,
              reading_status: meter.readingStatus,
              reading_date: meter.readingDate ? normalizeDate(meter.readingDate) : '',
              reason: meter.reason,
              consumption: meter.consumption.corrected
            }))
          };
        }
        
        // Service charges data
        if (appliedCorrections?.serviceCharges) {
          const serviceCharges = appliedCorrections?.serviceCharges?.allCharges.map(charge => ({
            key: charge.key,
            label: charge.chargeLabel,
            isActive: charge.status !== "Removed",
            fee: charge.amount
          }));
          
          step2Data.service = {
            serviceCharges: serviceCharges,
            reason: appliedCorrections?.serviceCharges?.reason
          };
        }
        
        // Penalty/Late fees data
        if (appliedCorrections?.lateFees) {
          step2Data.penalty = {
            isWaived: appliedCorrections?.lateFees?.waivedCompletely,
            adjustedAmount: appliedCorrections?.lateFees?.latePaymentFee?.corrected,
            reason: appliedCorrections?.lateFees?.reason
          };
        }
        
        // Consumer data
        if (appliedCorrections?.consumerData) {
          const consumerData: any = {};
          appliedCorrections.consumerData.fields?.forEach(field => {
            const camelKey = toCamelCase(field.fieldName);
            consumerData[camelKey] = field.newValue;
          });
          consumerData.reason = appliedCorrections?.consumerData?.reason || "";
          step2Data.data = consumerData;
        }
        
        // Template data
        if (appliedCorrections?.template) {
          step2Data.template = {
            templateId: appliedCorrections.template?.newTemplateId,
            reason: appliedCorrections.template?.reason
          };
        }
        
        stepHelpers?.setStepData(2, step2Data);
        
        // Mark as initialized to prevent re-running
        setIsDataInitialized(true);
      }
    }
  }, [action, correctionId, previewBill, billsList, isDataInitialized, stepHelpers]);


  //use this previewBill variable to set the data in setStepData for all steps, i have 4 steps, main correction data in step 2.
  const handleContinue = () => {
    clearValidationError?.();

    if (!selectedBill) {
      setValidationError?.("Please select a bill to continue.");
      return;
    }

    stepHelpers?.setStepData(currentStepIndex, {
      selectedBill,
      correctionId: correctionId,
    });

    onNext?.();
  };

  return (
    <div className="flex flex-col h-full gap-6">
      <p className="text-sm text-muted-foreground">
        Select a finalized bill to proceed.
      </p>

      <div className="relative">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          className="pl-9"
          placeholder="Search by name or account no"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      <div className="flex-1 space-y-3 overflow-y-auto">
        {billsList?.result?.map((bill) => {
          const isSelected = selectedBill?.id === bill.id;
          const billDetails = getBillDetailsConfig(bill);

          return (
            <Card
              key={bill.id}
              onClick={() => setSelectedBill(bill)}
              className={`cursor-pointer transition border ${
                isSelected
                  ? "border-primary bg-primary/5"
                  : "hover:border-muted-foreground/30"
              }`}
            >
              <CardContent className="p-5 space-y-4">
                {/* Header */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <h3 className="text-sm font-semibold">{bill?.billNumber}</h3>
                    <Badge className={getStatusBadgeClass(bill?.billStateLabel)}>
                      {bill?.billStateLabel}
                    </Badge>
                  </div>
                  <div className="text-lg font-semibold">
                    {currency} {bill?.billAmount}
                  </div>
                </div>

                <Separator />

                {/* Details */}
                <div className="grid grid-cols-5 gap-6">
                  {billDetails?.map((detail) => (
                    <ValueLabel
                      key={detail.label}
                      label={detail.label}
                      value={detail.value}
                    />
                  ))}
                </div>

                <ValueLabel
                  label="Service address"
                  value={bill?.consumerAddress ?? "NA"}
                />
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="flex justify-end pt-2">
        <Button onClick={handleContinue} disabled={!selectedBill}>
          Next
        </Button>
      </div>
    </div>
  );
};

export default BillSelectionStep;